import React from 'react';
import { ListChecks } from 'lucide-react';

const StandardsSelectionSection = React.memo(({ standards, selectedIds, onSelectionChange, primarySubject, gradeLevels, selectedDomains, disabled = false }) => {

    /**
     * Determines the reason why standards might not be showing and returns an appropriate message.
     * This provides better user feedback than a blank space.
     */
    const getPlaceholderMessage = () => {
        if (!primarySubject) {
            return "Please select a subject to see available standards.";
        }
        if (gradeLevels.length === 0) {
            return "Please select a grade level.";
        }
        if (selectedDomains.length === 0) {
            return "Please select a domain/topic to view relevant standards.";
        }
        // This message shows if everything is selected but the data file has no matching standards.
        return "No specific standards were found for the selected combination. The AI will still consider the subject, grade, and domain context you provided.";
    };

    // If there are no standards to display, show the placeholder box with a helpful message.
    if (!standards || standards.length === 0) {
        return (
            <div className={`mt-6 p-4 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-500 ${disabled ? 'opacity-70' : ''}`}>
                <div className="flex items-center mb-2">
                    <ListChecks className="h-5 w-5 mr-2 text-slate-400 flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-slate-600">Select Relevant Standards</h3>
                </div>
                <p className="pl-1">{getPlaceholderMessage()}</p>
                <p className="mt-2 pl-1 text-xs text-amber-700">Note: Standards data is continuously being updated.</p>
            </div>
        );
    }

    // If standards are available, render the selection list.
    return (
        <div className={`mt-6 p-4 border border-sky-200 rounded-lg bg-sky-50 ${disabled ? 'opacity-70' : ''}`}>
            <div className="flex items-center mb-3">
                <ListChecks className="h-5 w-5 mr-2 text-sky-600 flex-shrink-0" />
                <h3 className="text-lg font-semibold text-sky-700">Select Relevant Standards (Optional)</h3>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto p-1">
                {standards.map(standard => (
                    <label key={standard.id} className={`flex items-start space-x-2 p-2 hover:bg-sky-100 rounded-md transition-colors duration-150 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input type="checkbox" className="form-checkbox h-4 w-4 text-sky-600 border-slate-400 rounded focus:ring-sky-500 focus:ring-offset-1 mt-1 flex-shrink-0" checked={selectedIds.includes(standard.id)} onChange={() => !disabled && onSelectionChange(standard.id)} disabled={disabled} />
                        <div>
                            <span className="text-sm font-medium text-slate-700">{standard.code} ({standard.grade} - {standard.domain}{standard.cluster ? ` - ${standard.cluster}` : ''})</span>
                            {standard.description && <p className="text-xs text-slate-600">{standard.description}</p>}
                        </div>
                    </label>
                ))}
            </div>
            <p className="text-xs text-slate-500 mt-3 italic">
                If you select standards here, their codes and descriptions will be sent to the AI.
            </p>
        </div>
    );
});

export default StandardsSelectionSection;
